import { PaperClipIcon } from '@/components/icons/paperclip-icon'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label, LabelAsterisk } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import {
  CREATE_DATA_ASSET_ERROR,
  useCreateDataAssetMutation,
} from '@/hooks/api/use-create-data-asset-mutation'
import { DataAssetType, useDataAssetsQuery } from '@/hooks/api/use-data-assets-query'
import { T, useTranslate } from '@tolgee/react'
import { X } from 'lucide-react'
import React from 'react'

type FileWithDescription = {
  file: File
  fileName: string
  description: string
}

type ItemFormData = {
  filesWithDescriptions: FileWithDescription[]
}

type AddDataAssetDialogProps = Readonly<{
  open: boolean
  onOpenChange: (open: boolean) => void
}>

const MAX_FILES = 5
const MAX_FILE_SIZE = 5 * 1024 * 1024
const MAX_DESCRIPTION_LENGTH = 100

// Utility function to generate unique file name
const generateUniqueFileName = (fileName: string, existingNames: string[]): string => {
  const extensionRegex = /(\.[^/.]+)$/
  const match = extensionRegex.exec(fileName)

  const extension = match ? match[0] : ''
  const nameWithoutExtension = match ? fileName.slice(0, match.index) : fileName

  // Check if the original name exists (compare without extension)
  if (!existingNames.includes(nameWithoutExtension)) {
    return fileName
  }

  // Find the next available number
  let counter = 1
  let newFileName = `${nameWithoutExtension}_${counter}${extension}`

  while (existingNames.includes(`${nameWithoutExtension}_${counter}`)) {
    counter++
    newFileName = `${nameWithoutExtension}_${counter}${extension}`
  }

  return newFileName
}

// Utility function to create a new File object with a different name
const createFileWithNewName = (file: File, newName: string): File => {
  return new File([file], newName, {
    type: file.type,
    lastModified: file.lastModified,
  })
}

// Utility function to get file name without extension
const getFileNameWithoutExtension = (fileName: string): string => {
  return fileName.replace(/\.[^/.]+$/, '')
}

export function AddDataAssetDialog({ open, onOpenChange }: AddDataAssetDialogProps) {
  const { t } = useTranslate()
  const createDataAssetMutation = useCreateDataAssetMutation()
  const dataAssetsQuery = useDataAssetsQuery()
  const isSubmitting = createDataAssetMutation.isPending
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const [formData, setFormData] = React.useState<ItemFormData>({
    filesWithDescriptions: [],
  })

  React.useEffect(() => {
    if (open) {
      setFormData({
        filesWithDescriptions: [],
      })
    }
  }, [open])

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])

    // Check if adding these files would exceed the limit
    const totalFiles = formData.filesWithDescriptions.length + selectedFiles.length
    if (totalFiles > MAX_FILES) {
      toast.error(`You can only upload up to ${MAX_FILES} files. Please remove some files first.`)
      return
    }

    // Validate file sizes
    const invalidFiles = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE)
    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed the 5MB limit: ${invalidFiles.map((f) => f.name).join(', ')}`)
      return
    }

    // Get existing file names from the data catalog
    const existingNames = dataAssetsQuery.data?.data.map((asset) => asset.name) || []

    // Process each selected file to ensure unique names
    const processedFiles = selectedFiles.map((file) => {
      const uniqueFileName = generateUniqueFileName(file.name, existingNames)
      // If the name was changed, show a toast notification
      if (uniqueFileName !== file.name) {
        toast.success(`File renamed from "${file.name}" to "${uniqueFileName}" to avoid conflicts`)
      }

      return createFileWithNewName(file, uniqueFileName)
    })

    // Create FileWithDescription objects for new files
    const newFilesWithDescriptions = processedFiles.map((file) => ({
      file,
      fileName: getFileNameWithoutExtension(file.name), // Pre-populate with the file name without extension
      description: '',
    }))

    setFormData((prev) => ({
      ...prev,
      filesWithDescriptions: [...prev.filesWithDescriptions, ...newFilesWithDescriptions],
    }))

    if (e.target) {
      e.target.value = ''
    }
  }

  const handleFileRemove = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      filesWithDescriptions: prev.filesWithDescriptions.filter((_, i) => i !== index),
    }))
  }

  const handleDescriptionChange = (index: number, description: string) => {
    // Limit description to MAX_DESCRIPTION_LENGTH characters
    if (description.length <= MAX_DESCRIPTION_LENGTH) {
      setFormData((prev) => ({
        ...prev,
        filesWithDescriptions: prev.filesWithDescriptions.map((item, i) =>
          i === index ? { ...item, description } : item,
        ),
      }))
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const validateForm = (): boolean => {
    if (formData.filesWithDescriptions.length === 0) {
      toast.error('Please upload at least one file')
      return false
    }

    // Check if all descriptions are filled
    const emptyDescriptions = formData.filesWithDescriptions.filter(
      (item) => !item.description.trim(),
    )
    if (emptyDescriptions.length > 0) {
      toast.error('Please provide descriptions for all files')
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      // Create file descriptions mapping
      const fileDescriptions: Record<string, string> = {}
      formData.filesWithDescriptions.forEach(({ file, description }) => {
        fileDescriptions[file.name] = description
      })

      // Extract files
      const files = formData.filesWithDescriptions.map(({ file }) => file)

      await createDataAssetMutation.mutateAsync({
        dataType: 'DATA' as DataAssetType,
        files: files,
        fileDescriptions: JSON.stringify(fileDescriptions),
      })
      toast.success(t('dataCatalog.addButton.toast.success'))
      onOpenChange(false)
    } catch (error) {
      console.error('Delete failed:', error)

      let errorMessage = t('dataCatalog.addButton.toast.error')

      if (error instanceof Error && error.message && error.message !== CREATE_DATA_ASSET_ERROR) {
        errorMessage = error.message
      }

      toast.error(errorMessage)
    }
  }

  const fileCountText = (() => {
    if (formData.filesWithDescriptions.length === 0) {
      return ''
    }

    const fileCount = formData.filesWithDescriptions.length
    const fileSuffix = fileCount > 1 ? 's' : ''
    return `${fileCount} file${fileSuffix} selected`
  })()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[90vh] max-w-2xl flex-col'>
        <DialogHeader className='flex-shrink-0 px-1'>
          <DialogTitle>Add Item</DialogTitle>
          <p className='text-xs italic text-neutral-500'>
            <T keyName='dataCatalog.addButton.subtitle' />
          </p>
        </DialogHeader>

        <div className='max-h-[80vh] overflow-y-auto overflow-x-hidden'>
          <div className='grid gap-4'>
            <div className='flex items-center gap-3'>
              <div className='flex'>
                <Button
                  id='files'
                  variant='black'
                  title={t('dataCatalog.addButton.selectFile.label')}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting || formData.filesWithDescriptions.length >= MAX_FILES}
                  className='flex items-center gap-2'
                >
                  <span className='text-sm'>
                    <T keyName='dataCatalog.addButton.selectFile.title' />
                  </span>
                  <PaperClipIcon className='size-6 text-mango-200' />
                </Button>
                <Label htmlFor='files'>
                  <LabelAsterisk />
                </Label>
              </div>

              {fileCountText && <span className='text-sm text-gray-600'>{fileCountText}</span>}
            </div>

            <p className='text-sm font-light italic text-neutral-500'>
              <T keyName='dataCatalog.addButton.message' params={{ MAX_FILES }} />
            </p>

            <Input
              ref={fileInputRef}
              type='file'
              multiple
              accept='.csv,text/csv'
              className='hidden'
              onChange={handleFileUpload}
              disabled={isSubmitting}
            />

            {formData.filesWithDescriptions.map(({ file, fileName, description }, index) => (
              <div key={`${fileName}-${index}`}>
                <div className='flex flex-col'>
                  <Button
                    size='sm'
                    unstyled
                    onClick={() => handleFileRemove(index)}
                    disabled={isSubmitting}
                    className='ml-auto px-0 py-0'
                  >
                    <X className='size-4' />
                  </Button>
                  <div className='flex flex-col gap-4 pl-1'>
                    <div className='flex items-end gap-4 '>
                      <div className='flex flex-1 flex-col gap-2'>
                        <Label htmlFor={`filename-${index}`}>
                          File Name
                          <LabelAsterisk />
                        </Label>
                        <Input
                          required
                          id={`filename-${index}`}
                          type='text'
                          value={fileName}
                          disabled={true} // Always disabled - user cannot edit
                          className='w-full bg-gray-50'
                          placeholder='Add file name'
                        />
                      </div>

                      <div className='flex h-10 w-56 max-w-56 items-center gap-2 rounded border border-mango-100 bg-mango-50 p-2'>
                        <PaperClipIcon className='size-4 flex-shrink-0 text-mango-300' />
                        <span className='truncate text-sm text-neutral-500' title={file.name}>
                          {file.name}
                        </span>
                      </div>
                    </div>

                    <div className='flex flex-col gap-2'>
                      <Label htmlFor={`description-${index}`}>
                        File Description
                        <LabelAsterisk />
                      </Label>
                      <Input
                        required
                        id={`description-${index}`}
                        type='text'
                        placeholder='Add file description'
                        value={description}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        disabled={isSubmitting}
                        className='w-full'
                      />
                      <div className='text-right text-xs text-gray-500'>
                        {description.length}/{MAX_DESCRIPTION_LENGTH} characters
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className='flex-shrink-0 gap-2'>
          <Button
            variant={'mango-outline'}
            title={t('shared.cancelDialog.a11y')}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <T keyName='dataCatalog.addButton.cancel' />
          </Button>
          <Button
            onClick={handleSave}
            loading={isSubmitting}
            title={t('dataCatalog.addButton.add.label')}
            disabled={
              isSubmitting ||
              formData.filesWithDescriptions.length === 0 ||
              formData.filesWithDescriptions.some((item) => !item.description.trim())
            }
          >
            <T keyName='dataCatalog.addButton.add.title' />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
